import React from 'react'
import { Box } from '@mui/material'

export interface PrepListViewportRenderProps {
  width: number
  height: number
  scrollContainer: HTMLDivElement | null
  isFallback: boolean
}

interface PrepListViewportProps {
  children: (props: PrepListViewportRenderProps) => React.ReactNode
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void
}

const useSimpleLayout = true

const PrepListViewport: React.FC<PrepListViewportProps> = ({ children, onScroll }) => {
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const [, forceUpdate] = React.useReducer((value) => value + 1, 0)

  const handleScrollContainerRef = React.useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node
    if (node) {
      forceUpdate()
    }
  }, [])

  React.useEffect(() => {
    const element = scrollContainerRef.current
    if (!element || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(() => {
      forceUpdate()
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [])

  const renderProps: PrepListViewportRenderProps = {
    width: scrollContainerRef.current ? scrollContainerRef.current.clientWidth : 0,
    height: scrollContainerRef.current ? scrollContainerRef.current.clientHeight : 0,
    scrollContainer: scrollContainerRef.current,
    isFallback: useSimpleLayout,
  }

  return (
    <Box
      data-prep-viewport
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        borderRadius: 0,
      }}
    >
      {useSimpleLayout ? (
        <Box
          ref={handleScrollContainerRef}
          sx={{
            display: 'block',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            flex: 1,
            minHeight: 0,
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            borderRadius: 0,
          }}
          onScroll={onScroll}
        >
          {children(renderProps)}
        </Box>
      ) : (
        <Box
          ref={handleScrollContainerRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: 0,
          }}
          onScroll={onScroll}
        >
          {/* Placeholder for future virtualization */}
          {children(renderProps)}
        </Box>
      )}
    </Box>
  )
}

export default PrepListViewport
